import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Crown, Clock, TrendingUp, X } from 'lucide-react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

interface TierExpirationWarningModalProps {
  tierName: string;
  tierColor: string;
  daysUntilExpiration: number;
  gcNeededToMaintain?: number;
  onClose: () => void;
  isOpen: boolean;
}

export const TierExpirationWarningModal = ({
  tierName,
  tierColor,
  daysUntilExpiration,
  gcNeededToMaintain,
  onClose,
  isOpen,
}: TierExpirationWarningModalProps) => {
  const navigate = useNavigate();

  const handleClose = () => {
    onClose();
  };

  const handleGoToLoyaltyClub = () => {
    onClose();
    setTimeout(() => {
      navigate('/loyalty-club');
    }, 100);
  };

  const getUrgencyLevel = () => {
    if (daysUntilExpiration <= 1) return 'critical';
    if (daysUntilExpiration <= 3) return 'high';
    if (daysUntilExpiration <= 7) return 'medium';
    return 'low';
  };

  const urgency = getUrgencyLevel();
  
  const urgencyConfig = {
    critical: {
      iconColor: 'text-red-400',
      glowColor: 'rgba(239,68,68,0.2)',
      borderColor: 'rgba(239,68,68,0.3)',
    },
    high: {
      iconColor: 'text-orange-400',
      glowColor: 'rgba(249,115,22,0.2)',
      borderColor: 'rgba(249,115,22,0.3)',
    },
    medium: {
      iconColor: 'text-amber-400',
      glowColor: 'rgba(245,158,11,0.2)',
      borderColor: 'rgba(245,158,11,0.3)',
    },
    low: {
      iconColor: 'text-muted-foreground',
      glowColor: 'rgba(148,163,184,0.1)',
      borderColor: 'rgba(148,163,184,0.2)',
    },
  };

  const config = urgencyConfig[urgency];

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent 
        className="max-w-md w-[95vw] p-0 overflow-hidden border-0 bg-transparent shadow-none [&>button]:hidden"
        aria-describedby={undefined}
      >
        <div 
          className="relative rounded-2xl overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, rgba(15,23,42,0.98) 0%, rgba(30,41,59,0.95) 50%, rgba(15,23,42,0.98) 100%)',
            boxShadow: `0 0 40px ${config.glowColor}`,
            border: `1px solid ${config.borderColor}`,
          }}
        >
          {/* Top accent border */}
          <div 
            className="absolute inset-x-0 top-0 h-px"
            style={{
              background: `linear-gradient(to right, transparent, ${config.borderColor}, transparent)`,
            }}
          />

          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 z-50 w-9 h-9 rounded-full bg-muted/80 hover:bg-muted flex items-center justify-center transition-colors border border-border"
          >
            <X className="h-4 w-4 text-muted-foreground" />
          </button>

          {/* Content */}
          <div className="relative z-10 p-6 sm:p-8">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className="text-center space-y-5"
            >
              <DialogHeader className="space-y-4">
                {/* Icon */}
                <motion.div
                  initial={{ y: -10, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.1, duration: 0.4 }}
                  className="flex justify-center"
                >
                  <motion.div
                    animate={{ scale: [1, 1.05, 1] }}
                    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                    className="p-4 rounded-full"
                    style={{
                      background: `linear-gradient(135deg, ${config.glowColor}, transparent)`,
                      border: `1px solid ${config.borderColor}`,
                    }}
                  >
                    <AlertTriangle className={`h-10 w-10 ${config.iconColor}`} />
                  </motion.div>
                </motion.div>
                
                {/* Title */}
                <motion.div
                  initial={{ y: 10, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.2, duration: 0.4 }}
                >
                  <DialogTitle className={`text-2xl font-bold text-center ${config.iconColor}`}>
                    Tier Expiring Soon
                  </DialogTitle>
                </motion.div>
              </DialogHeader>

              <motion.div
                initial={{ y: 10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3, duration: 0.4 }}
                className="space-y-4"
              >
                {/* Tier Badge */}
                <div className="flex items-center justify-center gap-2">
                  <Crown className="h-5 w-5" style={{ color: tierColor }} />
                  <span className="text-xl font-semibold" style={{ color: tierColor }}>
                    {tierName}
                  </span>
                </div>

                {/* Countdown */}
                <div className="flex items-center justify-center gap-2 text-lg">
                  <Clock className={`h-5 w-5 ${config.iconColor}`} />
                  <span className="font-medium text-white">
                    {daysUntilExpiration === 1 
                      ? 'Expires tomorrow' 
                      : daysUntilExpiration === 0 
                        ? 'Expires today' 
                        : `Expires in ${daysUntilExpiration} days`}
                  </span>
                </div>

                {/* Description */}
                <DialogDescription className="text-center text-sm text-muted-foreground">
                  Your {tierName} tier status will expire soon. Continue playing to maintain your tier and keep enjoying exclusive benefits!
                </DialogDescription>

                {/* GC Needed */}
                {gcNeededToMaintain && gcNeededToMaintain > 0 && (
                  <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: 0.4, duration: 0.3 }}
                    className="bg-muted/50 p-4 rounded-xl border border-border"
                  >
                    <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground mb-2">
                      <TrendingUp className="h-4 w-4" />
                      <span>To maintain your tier:</span>
                    </div>
                    <div className="text-lg font-bold" style={{ color: tierColor }}>
                      Wager {gcNeededToMaintain.toLocaleString()} more GC
                    </div>
                  </motion.div>
                )}
              </motion.div>

              {/* Action Buttons */}
              <motion.div
                initial={{ y: 10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.5, duration: 0.4 }}
                className="flex gap-3 pt-2"
              >
                <Button 
                  onClick={handleGoToLoyaltyClub}
                  className="flex-1 h-12 font-semibold text-white border-0"
                  style={{ 
                    background: `linear-gradient(to right, ${tierColor}, ${tierColor}dd)`,
                  }}
                  size="lg"
                >
                  <Crown className="h-4 w-4 mr-2" />
                  View Status
                </Button>
                <Button 
                  onClick={handleClose}
                  variant="outline"
                  className="flex-1 h-12 bg-muted/80 border-border hover:bg-muted text-foreground"
                  size="lg"
                >
                  Got it
                </Button>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
